import { findAllConstructionTypesFromObjects } from 'components/WorkOrderConstructionTypesTabSelect';
import type { WorkOrderChangeObjectData } from 'gql/graphql';
import { ProjectMethodologyEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { BimAddOtherModal } from 'pages/Project/BimProject/BimCommonComponents/BimAddOtherModal';
import { CreateOther } from 'pages/Project/Common/Contract/OtherCreate';
import { useConstructionTypesListItems } from 'pages/Project/Common/hooks/useConstructionTypesListItems';
import type { IListItem } from 'types/Contract';
import { Text } from 'uikit';

import { useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { useAddOtherToBimWorkOrderTableData, useAddToBimWorkOrder } from './hooks';

interface IBimAddOtherTable {
    onCancel: () => void;
    handlerSuccess: () => void;
    constructionTypeId?: number;
    workOrderChangeObjectsData: WorkOrderChangeObjectData[];
    isFrameContract: boolean;
    workOrderStatus: WorkOrderStatusEnum | undefined;
    workOrderObject: IListItem<number> | undefined;
    hasOtherActsKc2: boolean;
}

export const BimAddOtherTable = (props: IBimAddOtherTable) => {
    const {
        onCancel,
        handlerSuccess,
        constructionTypeId,
        workOrderChangeObjectsData,
        isFrameContract,
        workOrderStatus,
        workOrderObject,
        hasOtherActsKc2,
    } = props;
    const { orderId } = useParams();
    const { getConstructionTypeName } = useConstructionTypesListItems();
    const tableRef = useRef<HTMLDivElement>(null);
    const workOrderId = useMemo(() => Number(orderId), [orderId]);

    const modalConfirmLabel = useMemo(() => {
        if (hasOtherActsKc2) {
            return 'Добавить в Акт';
        }

        return `Добавить в ${isFrameContract ? 'Наряд-Заказ' : 'Расчет'}`;
    }, [hasOtherActsKc2, isFrameContract]);

    const constructionTypes = useMemo(
        () => findAllConstructionTypesFromObjects({ workOrderChangeObjectsData }),
        [workOrderChangeObjectsData],
    );

    const workOrderObjects = useMemo(
        () =>
            workOrderChangeObjectsData.filter(
                (item) => item.parentId === null && constructionTypeId === item.constructionType.id,
            ),
        [constructionTypeId, workOrderChangeObjectsData],
    );

    const {
        tableRows,
        fetchStatus,
        tableColumns,
        tableFilters,
        haveCheckedOthers,
        checkedIds,
        refreshOthersDataAndSetCreatedId,
        onFiltersUpdated,
    } = useAddOtherToBimWorkOrderTableData({
        workOrderId,
        constructionTypeId,
        workOrderObjects,
        isFrameContract,
        workOrderStatus,
        workOrderObjectId: workOrderObject?.workOrderObjectId,
        tableRef,
    });

    const { addToWorkOrderChangeHandler, isAddInProgress } = useAddToBimWorkOrder({
        isFrameContract,
        handlerSuccess,
        constructionTypeId,
        workOrderId,
        checkedIds,
        workOrderStatus,
        workOrderObjectId: workOrderObject?.workOrderObjectId,
    });

    const isCreatingDisabled = useMemo(
        () => !isFrameContract && workOrderStatus === WorkOrderStatusEnum.OnFormation,
        [isFrameContract, workOrderStatus],
    );

    const isInProgress = useMemo(() => workOrderStatus === WorkOrderStatusEnum.InProgress, [workOrderStatus]);

    const informerMessage = useMemo(
        () =>
            isInProgress
                ? `Выбранные прочие затраты  будут применены к объекту «${workOrderObject?.label}»`
                : 'Выбранные прочие затраты по умолчанию будут применены ко всем объектам данного вида строительства. Для того, чтобы исключить объекты, нажмите кнопку раскрытия и снимите выделение.',
        [isInProgress, workOrderObject?.label],
    );

    const cancelTextHeader = useMemo(
        () => (isInProgress ? 'Добавление прочих в акт кс-2' : 'Отмена действия'),
        [isInProgress],
    );

    const textAddOtherAct = useMemo(
        () =>
            isInProgress ? (
                <Text
                    view="secondary"
                    align="left"
                    style={{
                        padding: '10px 24px',
                        color: '#002033',
                    }}
                >
                    Данные не будут сохранены.
                    <br />
                    Вы уверены, что хотите отменить добавление прочих в Акт КС-2?
                </Text>
            ) : (
                'Вы уверены, что хотите отменить? Заполненные данные не будут сохранены.'
            ),
        [isInProgress],
    );

    return (
        <BimAddOtherModal
            modalTitle={`Лимиты прочих затрат для вида строительства "${getConstructionTypeName(constructionTypeId)}"`}
            modalConfirmLabel={modalConfirmLabel}
            cancelTextHeader={cancelTextHeader}
            textConfirmation={textAddOtherAct}
            onConfirm={addToWorkOrderChangeHandler}
            onCancel={onCancel}
            haveCheckedOthers={haveCheckedOthers}
            isInProgress={isInProgress}
            isAddInProgress={isAddInProgress}
            tableColumns={tableColumns}
            tableRows={tableRows}
            tableFilters={tableFilters}
            onFiltersUpdated={onFiltersUpdated}
            fetchStatus={fetchStatus}
            informerMessage={informerMessage}
            leftPanel={
                <CreateOther
                    currentConstructionTypeId={constructionTypeId}
                    reloadHandler={refreshOthersDataAndSetCreatedId}
                    constructionTypes={constructionTypes}
                    methodology={ProjectMethodologyEnum.Bim}
                    workOrderId={workOrderId}
                    isDisabled={isCreatingDisabled}
                    workOrderStatus={workOrderStatus}
                    workOrderObjectId={workOrderObject?.workOrderObjectId}
                />
            }
            tableRef={tableRef}
        />
    );
};

BimAddOtherTable.displayName = 'BimAddOtherTable';
